import { createFeatureSelector, createSelector } from "@ngrx/store";
import { isNextWeek, isThisWeek, isToday, isTomorrow } from "src/app/shared/functions/date-ranges";
import { IAcademicClass, ITask } from "../../models/academic";
import { selectSelectedStudent, selectStudentById } from "../students/student.selectors";
import { AcademicClassState } from "./academic.reducer";

// Feature selector
export const selectAcademicClassState = createFeatureSelector<AcademicClassState>('academics');

// Selector for all classes
export const selectAllClasses = createSelector(
  selectAcademicClassState,
  (state: AcademicClassState) => state.classes
);

export const selectSelectedClass = createSelector(
  selectAcademicClassState,
  (state: AcademicClassState) => state.selectedClass
);

// Selector for loading state
export const selectClassesLoading = createSelector(
  selectAcademicClassState,
  (state: AcademicClassState) => state.loading
);

// Selector for error state
export const selectClassesError = createSelector(
  selectAcademicClassState,
  (state: AcademicClassState) => state.error
);

// Selector for all classes
export const selectAllTasks = createSelector(
  selectAllClasses,
  (classes: IAcademicClass[] = []) =>
    classes.flatMap(c => c.tasks || [])
);

// Selector for a specific class by id
export const selectClassById = (id: string) => createSelector(
  selectAllClasses,
  (classes) => classes.find(academicClass => academicClass.id === id)
);

export const selectTaskById = (id: string) => createSelector(
  selectAllTasks,
  (tasks) => tasks.find(task => task.id === id)
);

export const selectUpcomingTasks = (limit: number) => createSelector(
  selectAllTasks,
  (tasks: ITask[] = []) => {
    const today = new Date();

    return tasks
      .filter(task => new Date(task.dueDate) >= today)  // Filter out past tasks
      .sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime())  // Sort by upcoming due dates
      .slice(0, limit);  // Take a set number of tasks
  }
);

// Selector to find classes for the selected student
export const selectClassesForSelectedStudent = createSelector(
  selectSelectedStudent,
  selectAllClasses,
  (selectedStudent, classes) => {
    if (!selectedStudent) {
      return [];
    }
    return classes.filter(academicClass => selectedStudent.classIds.includes(academicClass.id));
  }
);

// Selector to find teachers for the student's classes
export const selectTeachersForSelectedStudentClasses = createSelector(
  selectClassesForSelectedStudent,
  (classes) => {
    if (!classes || classes.length === 0) {
      return [];
    }
    return classes.map(academicClass => academicClass.teacher).filter(Boolean);
  }
);

// Selector to find teachers for the student's classes
export const selectSubjectHeadsForSelectedStudentClasses = createSelector(
  selectClassesForSelectedStudent,
  (classes) => {
    if (!classes || classes.length === 0) {
      return [];
    }
    const academicClasses = classes.map(academicClass => academicClass.subject.head).filter(Boolean);
    return academicClasses
  }
);

export const selectAllTasksForSelectedStudent = createSelector(
  selectClassesForSelectedStudent,
  (classes) => {
    if (!classes || classes.length === 0) {
      return [];
    }
    const tasks = classes.flatMap(c => c.tasks || []);
    return tasks
  }
);

export const selectAllUpcomingTasksForSelectedStudent = createSelector(
  selectAllTasksForSelectedStudent,
  (tasks) => {
    const now = new Date();

    // Filter tasks between createDate and dueDate
    const upcomingTasks = tasks.filter(task =>
      now >= new Date(task.createDate) && now <= new Date(task.dueDate)
    );

    // Sort tasks by dueDate closest to now
    return upcomingTasks.sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime());
  }
);

// Select Tasks Due Today
export const selectTasksDueToday = createSelector(
  selectAllUpcomingTasksForSelectedStudent,
  (tasks: ITask[]) => tasks.filter(task => isToday(task.dueDate))
);

// Select Tasks Due Tomorrow
export const selectTasksDueTomorrow = createSelector(
  selectAllUpcomingTasksForSelectedStudent,
  (tasks: ITask[]) => tasks.filter(task => isTomorrow(task.dueDate))
);

// Select Tasks Due This Week
export const selectTasksThisWeek = createSelector(
  selectAllUpcomingTasksForSelectedStudent,
  (tasks: ITask[]) => tasks.filter(task => isThisWeek(task.dueDate))
);

// Select Tasks Due Next Week (Optional, assuming you want this functionality as well)
export const selectTasksNextWeek = createSelector(
  selectAllUpcomingTasksForSelectedStudent,
  (tasks: ITask[]) => tasks.filter(task => isNextWeek(task.dueDate))
);

// Select Tasks Due After Next Week (Upcoming Tasks)
export const selectFutureUpcomingTasks = createSelector(
  selectAllUpcomingTasksForSelectedStudent,
  (tasks: ITask[]) => {
    const now = new Date();
    const nextWeekEnd = new Date();
    nextWeekEnd.setDate(now.getDate() + (7 - now.getDay()) + 7); // End of next week

    return tasks.filter(task => new Date(task.dueDate) > nextWeekEnd);
  }
);

export const selectUpcomingTasksLength = createSelector(
  selectAllUpcomingTasksForSelectedStudent,
  (tasks: ITask[]) => tasks.length
);

export const selectUrgentTasksForStudentWithId = (id: string) => createSelector(
  selectAllTasks,
  selectStudentById(id),
  (allTasks, student) => {
    if (!student || !student.classIds || student.classIds.length === 0) {
      return [];
    }

    const studentClassIds = student.classIds;

    const today = new Date();
    const sevenDaysFromNow = new Date(today);
    sevenDaysFromNow.setDate(today.getDate() + 7);

    return allTasks
      .filter(task =>
        studentClassIds.includes(task.classId) &&
        new Date(task.dueDate) >= today &&
        new Date(task.dueDate) <= sevenDaysFromNow
      )
      .sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime()
      );
  }
);

export const selectTeacherForAcademicClassWithId = (id: string) => createSelector(
  selectClassById(id),
  (academicClass) => {
    return academicClass?.teacher
  }
)